import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RosterList } from "@/components/RosterList";
import {
  UserFocus, IdentificationCard, UsersThree, ShoppingBag, Question,
  ShieldWarning, VideoCamera, Sparkle, Trash, Scan, CaretRight, Clock,
  ImagesSquare, Plus, X, UserPlus, ArrowsMerge, UploadSimple, Camera, WarningOctagon, Power,
} from "@phosphor-icons/react";

const ROLE = {
  employee: { label: "Employee", cls: "text-[#32D74B] border-[#32D74B]/40 bg-[#32D74B]/10", Icon: UsersThree },
  customer: { label: "Customer", cls: "text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/10", Icon: ShoppingBag },
  known_threat: { label: "Watchlist", cls: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10", Icon: ShieldWarning },
  vendor: { label: "Worker / Vendor", cls: "text-[#FFD60A] border-[#FFD60A]/40 bg-[#FFD60A]/10", Icon: UsersThree },
  unknown: { label: "Unknown", cls: "text-[#A1A1AA] border-white/20 bg-white/5", Icon: Question },
};
const ROLE_OPTS = ["employee", "vendor", "known_threat"];
const TABS = [
  { key: "employee", label: "Employees", Icon: UsersThree, color: "#32D74B" },
  { key: "vendor", label: "Workers / Vendors", Icon: UsersThree, color: "#FFD60A" },
  { key: "unknown", label: "Awaiting ID", Icon: UserFocus, color: "#FF9F0A" },
  { key: "known_threat", label: "Watchlist", Icon: ShieldWarning, color: "#FF3B30" },
];

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Thumb({ b64 }) {
  if (!b64) return <div className="w-full aspect-[3/4] bg-[#1A1A1E] border border-white/10 flex items-center justify-center"><UserFocus size={28} weight="thin" className="text-[#3f3f46]" /></div>;
  return <img src={`data:image/jpeg;base64,${b64}`} alt="person" className="w-full aspect-[3/4] object-cover border border-white/10" />;
}

function Gallery({ images, pid }) {
  const imgs = (images || []).filter(Boolean);
  if (imgs.length <= 1) return null;
  return (
    <div className="flex gap-1 overflow-x-auto pb-0.5" data-testid={`person-gallery-${pid}`}>
      {imgs.map((img, i) => (
        <img key={i} src={`data:image/jpeg;base64,${img}`} alt="capture" className="h-11 w-9 object-cover border border-white/10 shrink-0" />
      ))}
    </div>
  );
}

function RoleBadge({ role }) {
  const r = ROLE[role] || ROLE.unknown;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 border ${r.cls}`}><r.Icon size={11} weight="bold" /> {r.label}</span>;
}

function Stat({ Icon, label, value, color }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-3 flex items-center gap-3" data-testid={`people-stat-${label.toLowerCase()}`}>
      <Icon size={22} weight="bold" className={color} />
      <div><p className="font-display font-black text-xl leading-none">{value}</p><p className="font-mono-data text-[9px] uppercase tracking-widest text-[#71717A] mt-1">{label}</p></div>
    </div>
  );
}

function NameCombo({ person, value, onChange, onSave, named, onPick, isNamed }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const others = named.filter((n) => n.id !== person.id).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const matches = q ? others.filter((n) => (n.name || "").toLowerCase().includes(q) || (n.department || "").toLowerCase().includes(q)) : others;
  return (
    <div className="relative flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
      <input data-testid={`person-name-input-${person.id}`} value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onKeyDown={(e) => { if (e.key === "Enter") { setOpen(false); onSave(); } }}
        placeholder={isNamed ? "Rename…" : "Name this person…"}
        autoComplete="off"
        className="w-full bg-[#1A1A1E] border border-white/10 text-xs text-white px-2 py-1.5 outline-none focus:border-[#32D74B]" />
      {open && matches.length > 0 && (
        <div data-testid={`name-suggest-menu-${person.id}`}
          className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-[#17171b] border border-[#32D74B]/40 shadow-xl">
          <div className="px-2 py-1 text-[9px] font-mono-data uppercase tracking-widest text-[#71717A] border-b border-white/5">
            Existing — pick to avoid a duplicate
          </div>
          {matches.map((n) => (
            <button key={n.id} data-testid={`name-suggest-${person.id}-${n.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setOpen(false); onPick(n); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#32D74B]/15 transition-colors text-left">
              {n.rep_image_b64
                ? <img src={`data:image/jpeg;base64,${n.rep_image_b64}`} alt="" className="w-7 h-9 object-cover border border-white/10 shrink-0" />
                : <div className="w-7 h-9 bg-[#1A1A1E] border border-white/10 flex items-center justify-center shrink-0"><UserFocus size={12} className="text-[#3f3f46]" /></div>}
              <span className="text-xs font-medium truncate">{n.name}</span>
              <span className="ml-auto font-mono-data text-[9px] text-[#71717A] shrink-0">×{n.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AssignMenu({ person, named, onAssign }) {
  const [open, setOpen] = useState(false);
  const options = named.filter((n) => n.id !== person.id);
  if (options.length === 0) return null;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`person-assign-btn-${person.id}`} onClick={() => setOpen((o) => !o)}
        title="This is actually one of my named people — merge into them"
        className="w-full flex items-center justify-center gap-1.5 text-[11px] font-mono-data uppercase tracking-wider py-1.5 bg-[#BF5AF2]/12 text-[#BF5AF2] border border-[#BF5AF2]/40 hover:bg-[#BF5AF2]/25 transition-colors">
        <ArrowsMerge size={13} weight="bold" /> Assign to person…
      </button>
      {open && (
        <div data-testid={`person-assign-menu-${person.id}`}
          className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-[#17171b] border border-[#BF5AF2]/40 shadow-xl">
          {options.map((n) => (
            <button key={n.id} data-testid={`assign-option-${n.id}`}
              onClick={() => { setOpen(false); onAssign(person, n); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#BF5AF2]/15 transition-colors text-left">
              {n.rep_image_b64
                ? <img src={`data:image/jpeg;base64,${n.rep_image_b64}`} alt="" className="w-8 h-10 object-cover border border-white/10 shrink-0" />
                : <div className="w-8 h-10 bg-[#1A1A1E] border border-white/10 flex items-center justify-center shrink-0"><UserFocus size={14} className="text-[#3f3f46]" /></div>}
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{n.name}</p>
                <p className="font-mono-data text-[9px] text-[#71717A]">×{n.count} seen</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
void AssignMenu;

// Click the photo -> a searchable name dropdown drops down so you can label/assign fast.
function PhotoNamePicker({ person, named, onPick, children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const all = named.filter((n) => n.id !== person.id).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const ql = q.trim().toLowerCase();
  const opts = ql ? all.filter((n) => (n.name || "").toLowerCase().includes(ql) || (n.department || "").toLowerCase().includes(ql)) : all;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid={`photo-pick-${person.id}`} onClick={() => { setOpen((o) => !o); setQ(""); }}
        title="Click to pick this person's name" className="block w-full group">
        {children}
        <span className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px] font-mono-data uppercase tracking-wider py-1 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <CaretRight size={10} weight="fill" /> pick name
        </span>
      </button>
      {open && (
        <div data-testid={`photo-name-menu-${person.id}`}
          className="absolute z-40 left-0 right-0 top-2 max-h-72 overflow-y-auto bg-[#17171b] border border-[#32D74B]/50 shadow-2xl">
          <div className="sticky top-0 bg-[#17171b] border-b border-white/5 p-1.5 z-10">
            <input autoFocus data-testid={`photo-name-search-${person.id}`} value={q}
              onChange={(e) => setQ(e.target.value)} placeholder={`Search ${all.length} staff…`}
              className="w-full bg-[#0F0F12] border border-white/10 text-xs text-white px-2 py-1.5 outline-none focus:border-[#32D74B]" />
          </div>
          {opts.length === 0 && <div className="px-2 py-3 text-[11px] text-[#64748B]">{all.length === 0 ? "No names yet — load your roster first." : "No match."}</div>}
          {opts.map((n) => (
            <button key={n.id} data-testid={`photo-name-opt-${person.id}-${n.id}`}
              onClick={() => { setOpen(false); onPick(n); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#32D74B]/15 transition-colors text-left">
              {n.rep_image_b64
                ? <img src={`data:image/jpeg;base64,${n.rep_image_b64}`} alt="" className="w-7 h-9 object-cover border border-white/10 shrink-0" />
                : <div className="w-7 h-9 bg-[#1A1A1E] border border-white/10 flex items-center justify-center shrink-0"><UserFocus size={12} className="text-[#3f3f46]" /></div>}
              <span className="text-xs font-medium truncate">{n.name}</span>
              {n.department && <span className="ml-auto font-mono-data text-[9px] text-[#71717A] shrink-0 truncate">{n.department}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function People() {
  const [all, setAll] = useState([]);
  const [stats, setStats] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("employee");
  const [cameras, setCameras] = useState([]);
  const [camId, setCamId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [autoprune, setAutoprune] = useState(true);
  const [keep, setKeep] = useState(3);
  const [autoWatch, setAutoWatch] = useState(true);
  const [viewerId, setViewerId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("employee");
  const [newDept, setNewDept] = useState("");
  const [bulkDept, setBulkDept] = useState("");
  const [newPhoto, setNewPhoto] = useState(null);
  const [creating, setCreating] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkItems, setBulkItems] = useState([]); // {name, image_b64}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [deptNames, setDeptNames] = useState([]);
  const [importing, setImporting] = useState(false);
  const [brightening, setBrightening] = useState(false);
  const [purging, setPurging] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const clearAllPeople = async () => {
    if (!window.confirm("Delete ALL people cards — named staff AND unnamed captures — for a totally fresh start? Analytics stay. New cards rebuild one face at a time as cameras see people.")) return;
    setClearingAll(true);
    try {
      const r = await api.post("/people/clear-all", {});
      toast.success(`Cleared ${r.data.removed} card(s) — starting fresh`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not clear people"); }
    finally { setClearingAll(false); }
  };
  const purgeUnnamed = async () => {
    if (!window.confirm("Delete ALL un-named detection cards (the old blurry/body-blob photos)? Enrolled & named staff are kept. New sharp face cards will rebuild as cameras see people.")) return;
    setPurging(true);
    try {
      const r = await api.post("/people/purge-unnamed", {});
      toast.success(`Cleared ${r.data.removed} unnamed card(s) — starting fresh with sharp faces`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not clear cards"); }
    finally { setPurging(false); }
  };
  const brightenPhotos = async () => {
    setBrightening(true);
    try {
      const r = await api.post("/people/enhance-photos", {});
      toast.success(`Brightened ${r.data.enhanced} photo(s) — faces should be clearer now`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not brighten photos"); }
    finally { setBrightening(false); }
  };
  const DEPT_OPTS = Array.from(new Set([
    ...deptNames, "Cashier", "Deli", "Meat", "Produce", "Grocery", "Dairy",
    "Bakery", "Seafood", "Entrance", "Office", "Exit", "Admin", "Management",
  ]));
  const dedupeFaces = async () => {
    setDeduping(true);
    try {
      const r = await api.post("/people/dedupe", {});
      toast.success(r.data.merged > 0 ? `Merged ${r.data.merged} duplicate face card(s)` : "No duplicates found — roster is clean");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Dedupe failed"); }
    finally { setDeduping(false); }
  };
  const importRoster = async (replace = false) => {
    if (replace && !window.confirm("This will DELETE all current employee names (including manually-added ones) and load only the official roster. Detected photos stay. Continue?")) return;
    setImporting(true);
    try {
      const r = await api.post("/people/import-roster", { replace });
      toast.success(`Roster loaded — ${r.data.created} added${r.data.removed ? `, ${r.data.removed} old names cleared` : ""} (${r.data.total} staff)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };
  const changeDept = async (p, department) => {
    try { await api.post(`/people/${p.id}/department`, { department }); toast.success(`${p.name || "Person"} → ${department || "no area"}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not set area"); }
  };
  const toggleActive = async (p) => {
    const nv = p.active === false; // becoming active if currently inactive
    try {
      await api.post(`/people/${p.id}/active`, { active: nv });
      toast.success(nv ? `${p.name || "Employee"} reactivated` : `${p.name || "Employee"} deactivated — no longer recognised or tracked`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update"); }
  };
  const { user } = useAuth();
  const isMaster = user?.role === "master" || user?.role === "admin";

  const load = useCallback(() => {
    api.get("/people").then((r) => {
      const list = [...(r.data.people || []), ...(r.data.pending || [])];
      list.sort((a, b) => new Date(b.last_seen || 0) - new Date(a.last_seen || 0));
      setAll(list); setStats(r.data.stats || {}); setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/cameras").then((r) => { const l = r.data || []; setCameras(l); if (l.length && !camId) setCamId(l[0].id); }).catch(() => {}); }, []); // eslint-disable-line

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const scanNow = async () => {
    if (!camId) return toast.error("Select a camera first");
    setScanning(true);
    try { const r = await api.post("/identify/scan", { camera_id: camId }); toast.success(`Detected ${r.data.detected} person(s) on ${r.data.camera}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const savePerson = async (p) => {
    const d = drafts[p.id] || {};
    const name = (d.name ?? p.name ?? "").trim();
    if (!name) return toast.error("Enter a name");
    // Prevent duplicate employees: if another named person already uses this exact
    // name, merge this record into them instead of creating a second "paula".
    const dupe = all.find((n) => n.id !== p.id && n.status === "named"
      && (n.name || "").trim().toLowerCase() === name.toLowerCase());
    if (dupe) return assignInto(p, dupe);
    try {
      await api.post(`/people/${p.id}/name`, { name, role: d.role || p.label || tab });
      toast.success(`Saved — every past & future sighting is now "${name}"`);
      setDrafts((x) => { const c = { ...x }; delete c[p.id]; return c; });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const changeRole = async (p, role) => { try { await api.post(`/people/${p.id}/role`, { role }); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const forget = async (p) => { try { await api.delete(`/people/${p.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };

  const pickNewPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
    setNewPhoto(b64);
  };
  const createEmployee = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Enter a name");
    setCreating(true);
    try {
      await api.post("/people/create", { name, role: newRole, department: newDept.trim(), image_b64: newPhoto || null });
      toast.success(`Added ${name} — now on the AI watchlist`);
      setShowAdd(false); setNewName(""); setNewPhoto(null); setNewRole("employee"); setNewDept("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add"); }
    finally { setCreating(false); }
  };
  const captureFromCam = async () => {
    if (!camId) return toast.error("Pick a camera first (top bar)");
    setCapturing(true);
    try {
      const r = await api.get(`/cameras/${camId}/frame?display=1`);
      if (!r.data?.b64) return toast.error("No live frame from that camera yet");
      setNewPhoto(`data:image/jpeg;base64,${r.data.b64}`);
      toast.success("Captured — now type the name and save");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Capture failed"); }
    finally { setCapturing(false); }
  };
  const addBulkFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    const read = (f) => new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.onerror = () => res(null); rd.readAsDataURL(f); });
    const roster = all.filter((p) => (p.label || "") === "employee" && (p.name || "").trim()).map((p) => p.name);
    const matchRoster = (raw) => {
      const r = raw.toLowerCase().trim();
      if (!r) return "";
      return roster.find((n) => n.toLowerCase() === r)
        || roster.find((n) => n.toLowerCase().startsWith(r) || r.startsWith(n.toLowerCase()))
        || roster.find((n) => n.toLowerCase().includes(r) || r.includes(n.toLowerCase().split(" ")[0]))
        || "";
    };
    const items = await Promise.all(arr.map(async (f) => {
      const raw = f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
      return { name: matchRoster(raw), image_b64: await read(f) };
    }));
    setBulkItems((p) => [...p, ...items.filter((i) => i.image_b64)]);
  };
  const submitBulk = async () => {
    const people = bulkItems.filter((i) => (i.name || "").trim()).map((i) => ({ name: i.name.trim(), role: "employee", department: bulkDept.trim(), image_b64: i.image_b64 }));
    if (!people.length) return toast.error("Add photos and give each a name");
    setBulkBusy(true);
    try {
      const r = await api.post("/people/bulk-create", { people });
      const made = r.data.created || 0, att = r.data.attached || 0;
      toast.success(`Enrolled ${made + att} headshot${(made + att) === 1 ? "" : "s"}${att ? ` (${att} matched to roster)` : ""}`);
      setShowBulk(false); setBulkItems([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk enroll failed"); }
    finally { setBulkBusy(false); }
  };
  const doFactoryReset = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/factory-reset", { confirm: resetText.trim() });
      const total = Object.values(r.data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Clean slate — wiped ${total} records. Ready for enrollment.`);
      setShowReset(false); setResetText(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed"); }
    finally { setResetting(false); }
  };
  const assignInto = async (source, target) => {
    if (!window.confirm(`Merge "${source.name || 'this record'}" into "${target.name}"? Their photos & sightings combine and the duplicate is removed.`)) return;
    try {
      await api.post(`/people/${source.id}/merge`, { target_id: target.id });
      toast.success(`Merged into ${target.name}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Merge failed"); }
  };

  useEffect(() => { api.get("/settings/people").then((r) => { setAutoprune(r.data.autoprune); setKeep(r.data.keep || 3); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/departments").then((r) => setDeptNames(r.data.names || [])).catch(() => {}); }, [all]);
  useEffect(() => { api.get("/settings/surveillance").then((r) => setAutoWatch(r.data.auto)).catch(() => {}); }, []);
  const toggleAutoWatch = async () => {
    const nv = !autoWatch; setAutoWatch(nv);
    try { await api.post("/settings/surveillance", { auto: nv }); toast.success(nv ? "24/7 Auto-Watch ON — the server keeps identifying & recording even with no screen open" : "Auto-Watch OFF"); }
    catch (e) { setAutoWatch(!nv); toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Master only"); }
  };
  const trimPerson = async (p) => {
    try { await api.post(`/people/${p.id}/images/trim`, { keep: 1 }); toast.success("Extra photos deleted — kept the latest one"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const addPhoto = async (pid, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await api.post(`/people/${pid}/images/add`, { image_b64: b64 });
      toast.success("Photo added — it won't be auto-deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  const removePhoto = async (pid, image_b64) => {
    try { await api.post(`/people/${pid}/images/remove`, { image_b64 }); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const clearPhoto = async (pid, name) => {
    if (!window.confirm(`Clear the ID photo & reference faces for ${name || "this person"}? Use this to fix a wrong match. Their card stays; re-enroll a correct headshot after.`)) return;
    try { await api.post(`/people/${pid}/clear-photo`); toast.success("ID photo cleared — enroll a correct headshot"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const cleanupAll = async () => {
    if (!window.confirm(`Delete extra photos and keep only the newest ${keep} per person?`)) return;
    try { const r = await api.post("/people/images/cleanup", { keep }); toast.success(`Cleaned up ${r.data.people_cleaned} ${r.data.people_cleaned === 1 ? "person" : "people"}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const toggleAutoprune = async () => {
    const nv = !autoprune; setAutoprune(nv);
    try { await api.post("/settings/people", { autoprune: nv, keep }); toast.success(nv ? `Daily auto-clean ON — keeps newest ${keep}` : "Daily auto-clean OFF"); }
    catch (e) { setAutoprune(!nv); toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Master only"); }
  };

  // A roster entry with no photo yet is just a name in the picker — don't clutter the
  // grid with empty cards. It still appears in the name dropdown (namedPeople).
  const _isEmptyRoster = (p) => p.status === "named" && !p.rep_image_b64 && !((p.images || []).length) && !((p.manual_images || []).length);
  const shown = all.filter((p) => (p.label || "unknown") === tab && !_isEmptyRoster(p));
  const rosterOnly = all.filter((p) => (p.label || "unknown") === tab && _isEmptyRoster(p)).length;
  const namedPeople = all.filter((p) => p.status === "named" && (p.name || "").trim());
  // Employee names for the Bulk Enroll dropdown — those still missing a photo come first.
  const empNames = all
    .filter((p) => (p.label || "") === "employee" && (p.name || "").trim())
    .sort((a, b) => (a.rep_image_b64 ? 1 : 0) - (b.rep_image_b64 ? 1 : 0) || a.name.localeCompare(b.name));

  return (
    <div data-testid="people-page" className="space-y-4">
      <div className="bg-[#121214] border border-white/10 p-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <IdentificationCard size={18} weight="bold" className="text-[#BF5AF2]" /> People &amp; Identification
          <span className="font-mono-data text-[9px] px-1.5 py-0.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 flex items-center gap-1"><Sparkle size={10} weight="fill" /> SELF-TRAINING</span>
          <button data-testid="people-autowatch-toggle" onClick={toggleAutoWatch} title="Server analyzes cameras 24/7, even with no screen open"
            className={`font-mono-data text-[9px] px-1.5 py-0.5 border flex items-center gap-1 transition-colors ${autoWatch ? "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40" : "bg-white/5 text-[#71717A] border-white/10"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${autoWatch ? "bg-[#32D74B] live-dot" : "bg-[#71717A]"}`} /> 24/7 AUTO-WATCH {autoWatch ? "ON" : "OFF"}
          </button>
        </h2>
        <p className="text-xs text-[#A1A1AA] leading-relaxed mt-1">
          Cameras auto-group people they keep seeing, sorted by most recent. Name an <span className="text-[#32D74B]">employee</span> once and it sticks to every past &amp; future frame of them. You don't need to name every <span className="text-[#0A84FF]">customer</span> — just flag theft-concerned ones to the <span className="text-[#FF3B30]">Watchlist</span>.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button data-testid="add-employee-btn" onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 transition-colors">
            <UserPlus size={14} weight="bold" /> Add Employee
          </button>
          <button data-testid="bulk-enroll-btn" onClick={() => setShowBulk(true)}
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/40 hover:bg-[#0A84FF]/25 transition-colors">
            <UploadSimple size={14} weight="bold" /> Bulk Enroll
          </button>
          <button data-testid="import-roster-btn" onClick={() => importRoster(false)} disabled={importing} title="Load the store's employee roster with departments"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#30D5C8]/15 text-[#30D5C8] border border-[#30D5C8]/40 hover:bg-[#30D5C8]/25 disabled:opacity-40 transition-colors">
            <UsersThree size={14} weight="bold" /> {importing ? "Loading…" : "Import Roster"}
          </button>
          <button data-testid="reset-roster-btn" onClick={() => importRoster(true)} disabled={importing} title="Clear ALL current names and load only the official roster"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 hover:bg-[#FF9F0A]/25 disabled:opacity-40 transition-colors">
            <ArrowsMerge size={14} weight="bold" /> Reset &amp; Load
          </button>
          <button data-testid="brighten-photos-btn" onClick={brightenPhotos} disabled={brightening} title="Brighten & sharpen all stored photos so dark faces are identifiable"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 hover:bg-[#BF5AF2]/25 disabled:opacity-40 transition-colors">
            <Sparkle size={14} weight="fill" /> {brightening ? "Brightening…" : "Brighten Photos"}
          </button>
          <button data-testid="dedupe-btn" onClick={dedupeFaces} disabled={deduping} title="Merge repeat face cards of the same employee"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 hover:bg-[#BF5AF2]/25 disabled:opacity-40 transition-colors">
            <ArrowsMerge size={14} weight="bold" /> {deduping ? "Merging…" : "Remove Duplicates"}
          </button>
          <button data-testid="purge-unnamed-btn" onClick={purgeUnnamed} disabled={purging} title="Delete all old blurry/body-blob cards and start fresh — named staff are kept"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 disabled:opacity-40 transition-colors">
            <Trash size={14} weight="bold" /> {purging ? "Clearing…" : "Clear Unnamed"}
          </button>
          <button data-testid="clear-all-people-btn" onClick={clearAllPeople} disabled={clearingAll} title="Delete ALL people (named + unnamed) for a totally fresh start — tag faces one at a time"
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF3B30]/25 text-[#FF3B30] border border-[#FF3B30]/60 hover:bg-[#FF3B30]/35 disabled:opacity-40 transition-colors">
            <Trash size={14} weight="fill" /> {clearingAll ? "Clearing…" : "Clear All People"}
          </button>
          <select data-testid="people-camera-select" value={camId} onChange={(e) => setCamId(e.target.value)} className="bg-[#1A1A1E] border border-white/10 text-xs text-white px-2 py-1.5 font-mono-data outline-none focus:border-[#BF5AF2]">
            {cameras.length === 0 && <option value="">No cameras</option>}
            {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button data-testid="identify-scan-btn" onClick={scanNow} disabled={scanning || !camId} className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 hover:bg-[#BF5AF2]/25 disabled:opacity-40 transition-colors">
            <Scan size={14} weight="bold" className={scanning ? "animate-pulse" : ""} /> {scanning ? "Scanning…" : "Scan Now"}
          </button>
          <button data-testid="people-cleanup-btn" onClick={cleanupAll} title="Delete extra photos across everyone" className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 hover:bg-[#FF9F0A]/25 transition-colors">
            <Trash size={14} weight="bold" /> Clean Up Photos (keep {keep})
          </button>
          <label data-testid="people-autoprune-toggle" title="Automatically delete extra photos once a day" className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 border border-white/10 text-[#A1A1AA] cursor-pointer hover:border-white/30">
            <input type="checkbox" checked={autoprune} onChange={toggleAutoprune} className="w-3.5 h-3.5 accent-[#32D74B] cursor-pointer" /> Auto-clean daily
          </label>
          {isMaster && (
            <button data-testid="factory-reset-btn" onClick={() => setShowReset(true)} title="Wipe all people & analytics for a clean slate"
              className="ml-auto flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/20 transition-colors">
              <WarningOctagon size={14} weight="bold" /> Factory Reset
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat Icon={UsersThree} label="Employees" value={stats.employees ?? 0} color="text-[#32D74B]" />
        <Stat Icon={ShoppingBag} label="Customers" value={stats.customers ?? 0} color="text-[#0A84FF]" />
        <Stat Icon={ShieldWarning} label="Watchlist" value={stats.watchlist ?? 0} color="text-[#FF3B30]" />
        <Stat Icon={UserFocus} label="AwaitingID" value={stats.pending ?? 0} color="text-[#FF9F0A]" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10" data-testid="people-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`people-tab-${t.key}`} onClick={() => { setTab(t.key); setSelected(null); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === t.key ? "text-white" : "text-[#71717A] border-transparent hover:text-white"}`}
            style={tab === t.key ? { borderColor: t.color, color: t.color } : {}}>
            <t.Icon size={15} weight="bold" /> {t.label} <span className="opacity-60">({all.filter((p) => (p.label || "unknown") === t.key).length})</span>
          </button>
        ))}
      </div>

      {!loaded ? (
        <div className="text-center text-[#71717A] font-mono-data text-sm py-10">Loading people…</div>
      ) : (
        <>
        {rosterOnly > 0 && tab === "employee" && (
          <div data-testid="roster-hint" className="mb-3 border border-[#30D5C8]/30 bg-[#30D5C8]/10 px-3 py-2 font-mono-data text-[11px] text-[#30D5C8]">
            {rosterOnly} roster staff loaded &amp; ready in the name picker. Click any detected photo below → search &amp; pick their name. Cards fill in as you tag them.
          </div>
        )}
        {tab === "employee" && <RosterList people={all.filter((p) => (p.label || "unknown") === "employee" && _isEmptyRoster(p))} onChange={load} />}
        {tab === "employee" && (stats.face_missing ?? 0) > 0 && (
          <div data-testid="face-missing-banner" className="mb-3 border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2 font-mono-data text-[11px] text-[#FF3B30] flex items-start gap-2">
            <WarningOctagon size={14} weight="bold" className="mt-0.5 shrink-0" />
            <span><span className="font-bold">{stats.face_missing} staff can't be face-recognised yet</span> — flagged below with <span className="font-bold">No Face ID</span>. Open each card and upload a clear, frontal, well-lit face photo so cameras can identify them across shifts.</span>
          </div>
        )}
        {shown.length === 0 ? (
        <div className="border border-white/10 p-6 text-center" data-testid="people-empty"><p className="font-mono-data text-xs text-[#71717A]">No unnamed {tab === "known_threat" ? "watchlist" : tab} photos to tag right now. {rosterOnly > 0 ? "All roster names are in the picker." : "As cameras see people, they appear here newest-first."}</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="people-grid">
          {shown.map((p) => {
            const d = drafts[p.id] || {};
            const isNamed = p.status === "named";
            const isSel = selected === p.id;
            const inactive = p.active === false;
            return (
              <div key={p.id} data-testid={`person-card-${p.id}`} onClick={() => setSelected(p.id)}
                className={`bg-[#121214] border p-3 space-y-2 cursor-pointer transition-all ${inactive ? "opacity-60 grayscale" : ""} ${isSel ? "border-[#BF5AF2] shadow-[0_0_0_1px_#BF5AF2]" : "border-white/10 hover:border-white/25"}`}>
                <div className="relative">
                  <PhotoNamePicker person={p} named={namedPeople} onPick={(t) => assignInto(p, t)}>
                    <Thumb b64={p.rep_image_b64} />
                  </PhotoNamePicker>
                  {!isNamed && p.images?.length > 1 && <button data-testid={`person-trim-${p.id}`} onClick={(e) => { e.stopPropagation(); trimPerson(p); }} title="Delete extra photos — keep just the latest" className="absolute top-1 right-1 flex items-center gap-1 bg-black/70 hover:bg-[#FF3B30] text-white text-[9px] font-mono-data px-1.5 py-0.5 transition-colors"><span data-testid={`person-gallery-count-${p.id}`}>{p.images.length}</span> shots <Trash size={9} weight="bold" /></button>}
                  {isSel && <span data-testid={`person-selected-${p.id}`} className="absolute top-1 left-1 flex items-center gap-1 bg-[#BF5AF2] text-white text-[10px] font-mono-data px-1.5 py-0.5"><CaretRight size={10} weight="fill" /> SELECTED</span>}
                  <button data-testid={`person-viewall-${p.id}`} onClick={(e) => { e.stopPropagation(); setViewerId(p.id); }}
                    className="absolute bottom-1 right-1 flex items-center gap-1 bg-black/75 hover:bg-[#BF5AF2] text-white text-[9px] font-mono-data uppercase tracking-wider px-1.5 py-1 transition-colors">
                    <ImagesSquare size={11} weight="bold" /> View all ({(p.images?.length || 0) + (p.manual_images?.length || 0)})
                  </button>
                </div>
                <Gallery images={!isNamed ? p.images : []} pid={p.id} />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isNamed ? <p className="font-display font-black tracking-tight text-sm truncate">{p.name}</p> : <RoleBadge role={p.label} />}
                    {isNamed && p.label === "employee" && p.face_ready === false && (
                      <span data-testid={`person-no-faceid-${p.id}`}
                        title={p.face_issue === "no-photo"
                          ? "No reference photo on file — upload a clear, frontal, well-lit face photo so cameras can recognise this person."
                          : "The stored photo has no detectable face — replace it with a clear, frontal, well-lit face photo."}
                        className="shrink-0 font-mono-data text-[9px] px-1.5 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 uppercase tracking-wider flex items-center gap-1">
                        <WarningOctagon size={10} weight="bold" /> No Face ID
                      </span>
                    )}
                    {!isNamed && p.likely_staff && (
                      <span data-testid={`person-likely-staff-${p.id}`} title={p.likely_reason || "Behaviour suggests this is a staff member"}
                        className="shrink-0 font-mono-data text-[9px] px-1.5 py-0.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 uppercase tracking-wider">Likely Staff</span>
                    )}
                    {inactive && <span data-testid={`person-inactive-badge-${p.id}`} className="shrink-0 font-mono-data text-[9px] px-1.5 py-0.5 bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40 uppercase tracking-wider">Inactive</span>}
                  </div>
                  <span className="font-mono-data text-[10px] text-[#71717A] shrink-0">×{p.count} seen</span>
                </div>
                {isNamed && p.label === "employee" && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <label className="font-mono-data text-[9px] uppercase tracking-widest text-[#71717A] flex items-center gap-1"><UsersThree size={10} weight="bold" /> Work Area / Department</label>
                    <select data-testid={`person-dept-select-${p.id}`} value={p.department || ""} onChange={(e) => changeDept(p, e.target.value)}
                      className="w-full mt-0.5 bg-[#1A1A1E] border border-white/10 text-xs text-white px-1.5 py-1.5 font-mono-data outline-none focus:border-[#30D5C8]">
                      <option value="">— No area (roams) —</option>
                      {DEPT_OPTS.map((dn) => <option key={dn} value={dn}>{dn}</option>)}
                    </select>
                  </div>
                )}
                {p.descriptor && <p className="text-[11px] text-[#A1A1AA] leading-snug line-clamp-2">{p.descriptor}</p>}
                <div className="space-y-0.5 font-mono-data text-[10px] text-[#64748B]">
                  <p className="flex items-center gap-1.5"><Clock size={11} weight="bold" /> First: {fmt(p.first_seen)}</p>
                  <p className="flex items-center gap-1.5"><VideoCamera size={11} weight="bold" /> Last: {fmt(p.last_seen)} · {p.last_camera || "—"}</p>
                </div>
                <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                  <NameCombo person={p} value={d.name ?? p.name ?? ""} onChange={(v) => setDraft(p.id, { name: v })} onSave={() => savePerson(p)} named={namedPeople} onPick={(t) => assignInto(p, t)} isNamed={isNamed} />
                  <select data-testid={`person-role-select-${p.id}`} value={d.role ?? p.label ?? tab} onChange={(e) => setDraft(p.id, { role: e.target.value })} className="bg-[#1A1A1E] border border-white/10 text-xs text-white px-1.5 py-1.5 font-mono-data outline-none focus:border-[#32D74B]">
                    {ROLE_OPTS.map((r) => <option key={r} value={r}>{ROLE[r].label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`person-save-${p.id}`} onClick={() => savePerson(p)} className="flex-1 text-xs font-mono-data uppercase tracking-wider py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 transition-colors">{isNamed ? "Update" : "Save Identity"}</button>
                  {isNamed && (
                    <button data-testid={`person-active-toggle-${p.id}`} onClick={() => toggleActive(p)}
                      title={inactive ? "Reactivate this employee" : "Deactivate (holiday / left) — stops recognition & tracking"}
                      className={`px-2 border transition-colors ${inactive ? "text-[#32D74B] border-[#32D74B]/40 hover:bg-[#32D74B]/15" : "text-[#FF9F0A] border-[#FF9F0A]/40 hover:bg-[#FF9F0A]/15"}`}>
                      <Power size={15} weight="bold" />
                    </button>
                  )}
                  <button data-testid={`person-forget-${p.id}`} onClick={() => forget(p)} title="Forget / discard" className="px-2 text-[#71717A] hover:text-[#FF3B30] border border-white/10 hover:border-[#FF3B30]/40 transition-colors"><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}

      <p className="text-center font-display font-bold tracking-[0.3em] uppercase text-sm text-[#0A84FF] pt-2" data-testid="people-branding">Powered by A V Tech</p>

      {viewerId && (() => {
        const p = all.find((x) => x.id === viewerId);
        if (!p) return null;
        const captured = (p.images || []).filter(Boolean);
        const manual = (p.manual_images || []).filter(Boolean);
        return (
          <div data-testid="person-viewer" onClick={() => setViewerId(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div onClick={(e) => e.stopPropagation()} className="bg-[#121214] border border-white/15 w-full max-w-3xl max-h-[88vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#121214] z-10">
                <div>
                  <h3 className="font-display font-black tracking-tight text-base">{p.name || "Unidentified person"}</h3>
                  <p className="font-mono-data text-[10px] text-[#71717A] mt-0.5">×{p.count} sightings · last seen {fmt(p.last_seen)} · {p.last_camera || "—"}</p>
                </div>
                <button data-testid="person-viewer-close" onClick={() => setViewerId(null)} className="text-[#71717A] hover:text-white p-1"><X size={20} weight="bold" /></button>
              </div>

              <div className="p-4 space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">Captured by camera ({captured.length})</h4>
                    <span className="font-mono-data text-[9px] text-[#64748B]">auto-kept: newest {keep}</span>
                  </div>
                  {captured.length ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="viewer-captured">
                      {captured.slice().reverse().map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={`data:image/jpeg;base64,${img}`} alt="capture" className="w-full aspect-[3/4] object-cover border border-white/10" />
                          <button onClick={() => removePhoto(p.id, img)} title="Delete this photo" className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF3B30] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={11} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="font-mono-data text-xs text-[#71717A] py-4">No camera captures stored yet.</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">Added manually ({manual.length})</h4>
                    <label data-testid="viewer-add-photo" className="flex items-center gap-1 cursor-pointer text-[10px] font-mono-data uppercase tracking-wider px-2.5 py-1.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 transition-colors">
                      <Plus size={12} weight="bold" /> {uploading ? "Uploading…" : "Add Photo"}
                      <input type="file" accept="image/*" className="hidden" disabled={uploading}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addPhoto(p.id, f); }} />
                    </label>
                    <button data-testid="viewer-clear-photo" onClick={() => clearPhoto(p.id, p.name)}
                      title="Clear ID photo (fix a wrong match)"
                      className="ml-2 flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-wider px-2.5 py-1.5 bg-[#FF3B30]/15 text-[#FF453A] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 transition-colors">
                      <Trash size={12} weight="bold" /> Clear Photo
                    </button>
                  </div>
                  {manual.length ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="viewer-manual">
                      {manual.slice().reverse().map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={`data:image/jpeg;base64,${img}`} alt="manual" className="w-full aspect-[3/4] object-cover border border-[#32D74B]/30" />
                          <button onClick={() => removePhoto(p.id, img)} title="Delete this photo" className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF3B30] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={11} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="font-mono-data text-xs text-[#71717A] py-4">No manual photos yet. Upload a clear headshot so this person is easier to recognise — these are never auto-deleted.</p>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showAdd && (
        <div data-testid="add-employee-modal" onClick={() => !creating && setShowAdd(false)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121214] border border-white/15 w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-display font-black tracking-tight text-base flex items-center gap-2">
                <UserPlus size={18} weight="bold" className="text-[#32D74B]" /> Add Employee
              </h3>
              <button data-testid="add-employee-close" onClick={() => setShowAdd(false)} className="text-[#71717A] hover:text-white p-1"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-start gap-3">
                <label data-testid="add-employee-photo-label" className="cursor-pointer shrink-0">
                  {newPhoto
                    ? <img src={newPhoto} alt="preview" className="w-24 h-28 object-cover border border-[#32D74B]/40" />
                    : <div className="w-24 h-28 bg-[#1A1A1E] border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-[#71717A] hover:border-[#32D74B]/50 transition-colors">
                        <ImagesSquare size={22} weight="thin" /><span className="text-[9px] font-mono-data uppercase">Add photo</span>
                      </div>}
                  <input data-testid="add-employee-photo-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickNewPhoto(f); }} />
                </label>
                <div className="flex-1 space-y-3">
                  <button type="button" data-testid="add-employee-capture" onClick={captureFromCam} disabled={capturing || !camId}
                    className="w-full flex items-center justify-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider py-1.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40 hover:bg-[#BF5AF2]/25 disabled:opacity-40 transition-colors">
                    <Camera size={13} weight="bold" /> {capturing ? "Capturing…" : `Capture from ${cameras.find((c) => c.id === camId)?.name || "camera"}`}
                  </button>
                  <div>
                    <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">Name</label>
                    <input data-testid="add-employee-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createEmployee()}
                      placeholder="e.g. Paula Rivera" autoFocus
                      className="w-full mt-1 bg-[#1A1A1E] border border-white/10 text-sm text-white px-2 py-2 outline-none focus:border-[#32D74B]" />
                  </div>
                  <div>
                    <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">Role</label>
                    <select data-testid="add-employee-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}
                      className="w-full mt-1 bg-[#1A1A1E] border border-white/10 text-sm text-white px-2 py-2 font-mono-data outline-none focus:border-[#32D74B]">
                      {ROLE_OPTS.map((r) => <option key={r} value={r}>{ROLE[r].label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">Department</label>
                    <input data-testid="add-employee-dept" value={newDept} onChange={(e) => setNewDept(e.target.value)} list="dept-opts"
                      placeholder="e.g. Deli, Checkout, Produce"
                      className="w-full mt-1 bg-[#1A1A1E] border border-white/10 text-sm text-white px-2 py-2 outline-none focus:border-[#32D74B]" />
                    <datalist id="dept-opts">
                      {DEPT_OPTS.map((d) => <option key={d} value={d} />)}
                    </datalist>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-[#71717A] leading-snug">Add a clear headshot so cameras recognise them faster. You can add more photos later from their profile.</p>
              <div className="flex gap-2">
                <button data-testid="add-employee-save" onClick={createEmployee} disabled={creating}
                  className="flex-1 text-xs font-mono-data uppercase tracking-wider py-2 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40 hover:bg-[#32D74B]/25 disabled:opacity-40 transition-colors">
                  {creating ? "Adding…" : "Add Employee"}
                </button>
                <button data-testid="add-employee-cancel" onClick={() => setShowAdd(false)} disabled={creating}
                  className="px-4 text-xs font-mono-data uppercase tracking-wider py-2 border border-white/10 text-[#A1A1AA] hover:border-white/30 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulk && (
        <div data-testid="bulk-enroll-modal" onClick={() => !bulkBusy && setShowBulk(false)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121214] border border-white/15 w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-display font-black tracking-tight text-base flex items-center gap-2">
                <UploadSimple size={18} weight="bold" className="text-[#0A84FF]" /> Bulk Enroll Employees
              </h3>
              <button data-testid="bulk-enroll-close" onClick={() => setShowBulk(false)} className="text-[#71717A] hover:text-white p-1"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <label data-testid="bulk-drop" className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 py-8 cursor-pointer hover:border-[#0A84FF]/50 transition-colors">
                <ImagesSquare size={30} weight="thin" className="text-[#71717A]" />
                <span className="text-xs font-mono-data uppercase tracking-wider text-[#A1A1AA]">Drop or select employee photos (multiple)</span>
                <span className="text-[10px] text-[#64748B]">Then pick each employee from the dropdown (staff needing a photo shown first)</span>
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => { addBulkFiles(e.target.files); e.target.value = ""; }} />
              </label>
              {bulkItems.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="bulk-list">
                  {bulkItems.map((it, i) => (
                    <div key={i} className="bg-[#1A1A1E] border border-white/10 p-2 space-y-2">
                      <div className="relative">
                        <img src={it.image_b64} alt="" className="w-full aspect-[3/4] object-cover border border-white/10" />
                        <button onClick={() => setBulkItems((p) => p.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF3B30] text-white p-1"><X size={11} weight="bold" /></button>
                      </div>
                      <select data-testid={`bulk-name-${i}`} value={empNames.some((e) => e.name === it.name) ? it.name : ""}
                        onChange={(e) => setBulkItems((p) => p.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                        className="w-full bg-[#0F0F12] border border-white/10 text-xs px-2 py-1.5 outline-none focus:border-[#0A84FF] text-white">
                        <option value="">— pick employee —</option>
                        {empNames.map((e) => (
                          <option key={e.id} value={e.name}>{e.name}{e.rep_image_b64 ? " ✓" : ""}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-white/10 flex items-center gap-2">
              <input data-testid="bulk-dept" value={bulkDept} onChange={(e) => setBulkDept(e.target.value)} list="dept-opts-bulk"
                placeholder="Department (applies to all)"
                className="bg-[#0F0F12] border border-white/10 text-xs px-2 py-2 outline-none focus:border-[#0A84FF] w-48" />
              <datalist id="dept-opts-bulk">
                {DEPT_OPTS.map((d) => <option key={d} value={d} />)}
              </datalist>
              <span className="font-mono-data text-xs text-[#71717A] mr-auto">{bulkItems.length} photo{bulkItems.length === 1 ? "" : "s"}</span>
              <button data-testid="bulk-enroll-submit" onClick={submitBulk} disabled={bulkBusy || bulkItems.length === 0}
                className="text-xs font-mono-data uppercase tracking-wider px-4 py-2 bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/40 hover:bg-[#0A84FF]/25 disabled:opacity-40 transition-colors">
                {bulkBusy ? "Enrolling…" : `Enroll ${bulkItems.length}`}
              </button>
              <button onClick={() => { setBulkItems([]); }} disabled={bulkBusy || !bulkItems.length}
                className="text-xs font-mono-data uppercase tracking-wider px-3 py-2 border border-white/10 text-[#A1A1AA] hover:border-white/30 disabled:opacity-40 transition-colors">Clear</button>
            </div>
          </div>
        </div>
      )}

      {showReset && (
        <div data-testid="factory-reset-modal" onClick={() => !resetting && setShowReset(false)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121214] border border-[#FF3B30]/40 w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-display font-black tracking-tight text-base flex items-center gap-2 text-[#FF3B30]">
                <WarningOctagon size={18} weight="bold" /> Factory Reset
              </h3>
              <button data-testid="factory-reset-close" onClick={() => setShowReset(false)} className="text-[#71717A] hover:text-white p-1"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                This permanently deletes <span className="text-white">all people, metrics, alerts, events, heatmaps and analysis history</span>. Your cameras, users and settings are kept. This cannot be undone.
              </p>
              <p className="font-mono-data text-[11px] text-[#71717A] uppercase tracking-widest">Type RESET to confirm</p>
              <input data-testid="factory-reset-input" value={resetText} onChange={(e) => setResetText(e.target.value)} autoFocus placeholder="RESET"
                className="w-full bg-[#1A1A1E] border border-white/10 text-sm px-2 py-2 outline-none focus:border-[#FF3B30]" />
              <div className="flex gap-2">
                <button data-testid="factory-reset-confirm" onClick={doFactoryReset} disabled={resetting || resetText.trim().toUpperCase() !== "RESET"}
                  className="flex-1 text-xs font-mono-data uppercase tracking-wider py-2 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 disabled:opacity-40 transition-colors">
                  {resetting ? "Wiping…" : "Wipe Everything"}
                </button>
                <button onClick={() => setShowReset(false)} disabled={resetting}
                  className="px-4 text-xs font-mono-data uppercase tracking-wider py-2 border border-white/10 text-[#A1A1AA] hover:border-white/30 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
